import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import Layout from "@/components/Layout";
import Marketing from "@/pages/Marketing";
import Auth from "@/pages/Auth";
import Setup from "@/pages/Setup";
import Search from "@/pages/Search";
import Directory from "@/pages/Directory";
import Records from "@/pages/Records";
import PatientProfile from "@/pages/PatientProfile";
import LogVisit from "@/pages/LogVisit";
import Consultants from "@/pages/Consultants";
import Overview from "@/pages/Overview";
import Settings from "@/pages/Settings";
import ResetPassword from "@/pages/ResetPassword";
import Admin from "@/pages/Admin";
import AdminUsers from "@/pages/AdminUsers";
import Billing from "@/pages/Billing";
import ConsultantReport from "@/pages/ConsultantReport";
import Calendar from "@/pages/Calendar";
import { initAnalytics, trackPageView } from "@/lib/analytics";
import VerifyPhone from "@/pages/VerifyPhone";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import DataDeletion from "@/pages/DataDeletion";
import { HOME, PHONE_PATH, needsPhoneCheck } from "@/lib/api";

const Loading = () => (
  <div className="grid min-h-screen place-items-center text-sm font-semibold text-slate-500">Loading patient records…</div>
);

function Gate({ children, needClinic = true }) {
  const { user, clinic, subscription, isSuperAdmin } = useApp();
  const { pathname } = useLocation();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  // A new owner who hasn't confirmed their number goes there first, from anywhere.
  if (needsPhoneCheck(user)) return pathname === PHONE_PATH ? children : <Navigate to={PHONE_PATH} replace />;
  if (pathname === PHONE_PATH) return <Navigate to={clinic ? HOME : "/setup"} replace />;
  if (needClinic && !clinic) return <Navigate to="/setup" replace />;
  const locked = clinic && subscription && subscription.has_access === false && !isSuperAdmin;
  const exempt = pathname.startsWith("/app/billing") || pathname.startsWith("/app/settings");
  if (locked && !exempt) return <Navigate to="/app/billing" replace />;
  return children;
}

function OwnerOnly({ children }) {
  const { isOwner } = useApp();
  return isOwner ? children : <Navigate to="/app" replace />;
}

function AdminOnly({ children }) {
  const { isSuperAdmin } = useApp();
  return isSuperAdmin ? children : <Navigate to="/app" replace />;
}

function Analytics() {
  const { pathname } = useLocation();
  React.useEffect(() => { initAnalytics(); }, []);
  React.useEffect(() => { trackPageView(pathname); }, [pathname]);
  return null;
}

// Public marketing page. Signed-in visitors are sent straight into the app, exactly as before;
// anonymous visitors (and visitors whose session is still being checked) see the landing page.
function Landing() {
  const { user, clinic } = useApp();
  if (user) return <Navigate to={needsPhoneCheck(user) ? PHONE_PATH : clinic ? HOME : "/setup"} replace />;
  return <Marketing />;
}

// /login is sign-in for existing users; /start is onboarding (new clinic or join with a staff code).
function AuthRoute({ entry }) {
  const { user, clinic } = useApp();
  const { state } = useLocation();
  if (user === null) return <Loading />;
  if (user) return <Navigate to={needsPhoneCheck(user) ? PHONE_PATH : clinic ? HOME : "/setup"} replace />;
  // Old deep links sent sign-up modes to /login — move them to /start.
  if (entry === "login" && (state?.mode === "clinic" || state?.mode === "join")) return <Navigate to="/start" state={state} replace />;
  return <Auth key={entry} entry={entry} />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AppProvider>
        <Toaster position="top-center" theme="dark" richColors />
        <Analytics />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<AuthRoute entry="login" />} />
          <Route path="/start" element={<AuthRoute entry="start" />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/setup" element={<Gate needClinic={false}><Setup /></Gate>} />
          <Route path="/verify-phone" element={<Gate needClinic={false}><VerifyPhone /></Gate>} />
          <Route path="/app" element={<Gate><Layout /></Gate>}>
            <Route index element={<Search />} />
            <Route path="calendar" element={<Calendar />} />
            <Route path="directory" element={<Directory />} />
            <Route path="records" element={<Records />} />
            <Route path="patients/:id" element={<PatientProfile />} />
            <Route path="log-visit" element={<LogVisit />} />
            <Route path="consultants" element={<Consultants />} />
            <Route path="consultants/report" element={<OwnerOnly><ConsultantReport /></OwnerOnly>} />
            <Route path="overview" element={<OwnerOnly><Overview /></OwnerOnly>} />
            <Route path="settings" element={<Settings />} />
            <Route path="billing" element={<Billing />} />
            <Route path="admin" element={<AdminOnly><Admin /></AdminOnly>} />
            <Route path="admin/users" element={<AdminOnly><AdminUsers /></AdminOnly>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppProvider>
    </BrowserRouter>
  );
}
